import * as yup from 'yup';

import { getSupportedLocales, i18n } from '@/plugins/i18n';
import type { Locales } from '@/services/types/api.types';

const { t } = i18n.global;

export interface LocalizableStringValidationOptions {
  nullable?: boolean;
  optional?: boolean;
}

type LocalizableStringSchema = {
  [Locale in Locales]?: yup.StringSchema<string | undefined>;
};

export const localizableString = (
  languageEnabled?: Locales[] | null,
  options: LocalizableStringValidationOptions = {},
): yup.ObjectSchema<yup.AnyObject> => {
  const { nullable = false, optional = false } = options;
  const validationSchema =
    languageEnabled?.reduce<LocalizableStringSchema>((schema, locale) => {
      let validator = yup.string();
      if (nullable) {
        validator = validator.nullable() as yup.StringSchema<string | undefined>;
      }
      if (!optional) {
        validator = validator.required();
      }
      return { ...schema, [locale]: validator };
    }, {}) ?? {};
  return yup.object().shape(validationSchema);
};

export const localizableEditorString = (languageEnabled?: Locales[] | null): yup.ObjectSchema<yup.AnyObject> => {
  const validationSchema =
    languageEnabled?.reduce<LocalizableStringSchema>((schema, locale) => {
      return {
        ...schema,
        [locale]: yup
          .string()
          .required()
          .test('not-empty', '${path} is a required field', (value) => {
            const element = document.createElement('div');
            element.innerHTML = value;
            return element.innerText?.trim() !== '';
          }),
      };
    }, {}) ?? {};
  return yup.object().shape(validationSchema);
};

export const languageEnabledArray = (
  languageEnabled?: Locales[] | null,
): yup.ArraySchema<(string | undefined)[] | undefined, yup.AnyObject, '', ''> => {
  return yup
    .array()
    .of(yup.string())
    .test(
      'at-least-one-language',
      () => t('validation.min_one_language'),
      (values) => {
        const locales = languageEnabled ?? getSupportedLocales();
        return (values?.filter((x) => locales.includes(x as Locales)).length ?? 0) > 0;
      },
    );
};

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
// Read when a value is checked, so the message follows the administrator's language.
const slugFormatMessage = () => t('validation.slug.format');

export const slug = (): yup.StringSchema<string | undefined, yup.AnyObject> => {
  return yup
    .string()
    .matches(SLUG_PATTERN, { message: slugFormatMessage, excludeEmptyString: true })
    .required(() => t('validation.slug.required'));
};

/** A slug a record may also go without: an empty field means none. */
export const optionalSlug = () =>
  yup.string().nullable().matches(SLUG_PATTERN, { message: slugFormatMessage, excludeEmptyString: true });
